<template>
  <div>
    <div class="row mt-4">
      <div class="col-md-7">
        <h1 class="h3">
          <a-icon type="arrow-left" class="mr-2" style="cursor: pointer;" @click="$router.back()" />
          {{ $t('product.productTags.manage.title') }}
        </h1>
      </div>
      <div class="col-md-5 text-right">
        <a-button
          type="primary"
          size="large"
          :disabled="!activeTagKey"
          @click="onShowPicker"
        >
          <a-icon type="plus" /> {{ $t('product.productTags.manage.add_product') }}
        </a-button>
      </div>
    </div>

    <div class="mt-4">
      <a-tabs 
        v-if="tags.length > 0" 
        class="pca-tab-product" 
        :active-key="activeTagKey" 
        @change="onTagTabChange"
      >
        <a-tab-pane v-for="tag in tags" :key="tag.channel_product_tag.id" :tab="tag.name" />
      </a-tabs>

      <div v-if="activeTagKey">
        <a-table
          class="product-list mt-3 bg-white"
          :data-source="items"
          :row-key="(record) => record.id"
          :columns="columns"
          :loading="isLoading"
          :pagination="pagination"
          @change="onTableChange"
        >
          <template slot="product" slot-scope="text, record">
            <div class="d-flex align-items-center">
              <div class="mr-3 border rounded overflow-hidden" style="width: 50px; height: 50px;">
                <img 
                  :src="(record.product && record.product.url_images && record.product.url_images[0]) || 'https://via.placeholder.com/50'" 
                  class="w-100 h-100 object-fit-cover"
                  style="object-fit: cover;"
                />
              </div>
              <div>
                <div class="font-weight-bold text-dark">
                  {{ record.title || '-' }}
                </div>
                <div class="text-muted small">
                  SKU: {{ record.sku || '-' }}
                </div>
              </div>
            </div>
          </template>
          <template slot="action" slot-scope="record">
            <div class="text-right">
              <a-popconfirm
                :title="$t('product.productTags.manage.remove_product') + '?'"
                @confirm="onDeleteProduct(record)"
              >
                <a-button type="link" class="text-danger p-0">
                  <a-icon type="delete" />
                </a-button>
              </a-popconfirm>
            </div>
          </template>
        </a-table>
      </div>
      <div v-else-if="!isLoading" class="text-center py-5 bg-white rounded border">
        <a-empty :description="tags.length > 0 ? $t('product.productTags.manage.empty_description') : $t('product.productTags.manage.no_tags')" />
      </div>
    </div>

    <a-modal
      v-model="showPicker"
      :title="$t('product.productTags.manage.modal_title')"
      width="900px"
      :footer="null"
      :destroy-on-close="true"
    >
      <div class="mb-3">
        <a-input-search
          v-model="pickerKeyword"
          :placeholder="$t('product.productTags.manage.search_placeholder')"
          enter-button
          size="large"
          @search="onSearchPicker"
        />
      </div>
      <a-table
        :data-source="pickerItems"
        :columns="pickerColumns"
        :loading="isLoadingPicker"
        :pagination="pickerPagination"
        :row-key="record => `${record.product_id}.${record.id}`"
        size="small"
        @change="onPickerTableChange"
      >
        <template slot="product" slot-scope="text, record">
          <div class="d-flex align-items-center py-2">
            <div class="mr-3 border rounded overflow-hidden" style="width: 40px; height: 40px;">
              <img 
                :src="record.image_url || 'https://via.placeholder.com/40'" 
                class="w-100 h-100 object-fit-cover"
                style="object-fit: cover;"
              />
            </div>
            <div>
              <div class="font-weight-bold text-dark small" style="line-height:1.2">
                {{ record.productTitle || '-' }}
              </div>
              <div class="text-muted small" style="font-size: 11px;">
                SKU: {{ record.sku || '-' }}
              </div>
              <div v-if="record.catalogTitle" class="text-muted small" style="font-size: 11px;">
                Variant: {{ record.catalogTitle }}
              </div>
            </div>
          </div>
        </template>
        <template slot="action" slot-scope="record">
          <a-button 
            type="primary" 
            size="small" 
            shape="round" 
            :disabled="isTagged(record)"
            @click="onAddProduct(record)"
          >
            {{ isTagged(record) ? $t('product.productTags.manage.tagged') : $t('product.productTags.manage.add') }}
          </a-button>
        </template>
      </a-table>
    </a-modal>
  </div>
</template>

<script>
import { getTags, getProductsInTag, addProductToTag, deleteProductFromTag, getChannelProducts } from '@/api/product-tags'

export default {
  name: 'ManageProductTags',
  data() {
    return {
      isLoading: false,
      tags: [],
      activeTagKey: null,
      items: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      showPicker: false,
      isLoadingPicker: false,
      pickerKeyword: '',
      pickerItems: [],
      pickerPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('product.productTags.manage.table.product'),
          key: 'product',
          scopedSlots: { customRender: 'product' },
        },
        {
          title: '',
          key: 'action',
          width: 80,
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
    pickerColumns() {
      return [
        {
          title: this.$t('product.productTags.manage.table.product'),
          key: 'product',
          scopedSlots: { customRender: 'product' },
        },
        {
          title: '',
          key: 'action',
          width: 80,
          scopedSlots: { customRender: 'action' },
        },
      ]
    },
    channelId() {
      return this.$route.params.id
    },
    locale() {
      return this.$store.state.settings.locale
    },
  },
  watch: {
    locale() {
      this.initPage()
    },
  },
  mounted() {
    this.initPage()
  },
  methods: {
    async initPage() {
      this.isLoading = true
      await this.fetchTags()
      if (this.tags.length > 0) {
        const queryTagId = this.$route.query.tag_id
        const tagExists = this.tags.some(t => t.channel_product_tag.id === queryTagId)
        
        if (queryTagId && tagExists) {
          this.activeTagKey = queryTagId
        } else {
          this.activeTagKey = this.tags[0].channel_product_tag.id
        }
        await this.fetchData()
      } else {
        this.isLoading = false
      }
    },
    async fetchTags() {
      try {
        const params = {
          active_only: true,
          page: 1,
          size: 1000,
          sort: 'asc',
          order_by: 'channelProductTag.sequence',
        }
        const { data } = await getTags(this.channelId, params)
        this.tags = data.content || []
      } catch (error) {
        console.error('Failed to fetch tags', error)
      }
    },
    async fetchData() {
      if (!this.activeTagKey) return
      this.isLoading = true
      try {
        const params = {
          warehouse_id: this.$route.query.warehouse_id,
          page: this.pagination.current,
          size: this.pagination.pageSize,
        }
        const { data } = await getProductsInTag(this.channelId, this.activeTagKey, params)
        this.items = data.content || []
        this.pagination.total = data.total_elements || 0
      } catch (error) {
        this.$message.error(this.$t('product.productTags.messages.fetch_failed'))
      } finally {
        this.isLoading = false
      }
    },
    onTagTabChange(key) {
      this.activeTagKey = key
      this.pagination.current = 1
      this.fetchData()
    },
    onTableChange(pagination) {
      this.pagination.current = pagination.current
      this.fetchData()
    },
    onShowPicker() {
      this.showPicker = true
      this.pickerKeyword = ''
      this.pickerPagination.current = 1
      this.fetchPickerProducts()
    },
    async fetchPickerProducts() {
      this.isLoadingPicker = true
      try {
        const params = {
          workspace_id: this.$route.query.workspace_id,
          business_id: this.$route.query.business_id,
          channel_code: this.$route.query.channel_code || 'distributor',
          sales_channel_id: this.$route.query.sales_channel_id,
          search_query: this.pickerKeyword,
          page: this.pickerPagination.current,
          limit: this.pickerPagination.pageSize,
        }
        const { data: response } = await getChannelProducts(this.channelId, params)
        
        const flatItems = []
        const products = response.data || []
        
        products.forEach(product => {
          if (product.catalogs && product.catalogs.length > 0) {
            product.catalogs.forEach(catalog => {
              flatItems.push({
                ...catalog,
                productTitle: product.title,
                catalogTitle: catalog.title !== product.title ? catalog.title : null,
                image_url: product.showimg_url,
                product_id: catalog.product_id,
              })
            })
          }
        })

        this.pickerItems = flatItems
        this.pickerPagination.total = response.total_row || 0
      } catch (error) {
        this.$message.error(this.$t('product.productTags.manage.messages.fetch_products_failed'))
      } finally {
        this.isLoadingPicker = false
      }
    },
    onPickerTableChange(pagination) {
      this.pickerPagination.current = pagination.current
      this.fetchPickerProducts()
    },
    onSearchPicker() {
      this.pickerPagination.current = 1
      this.fetchPickerProducts()
    },
    isTagged(record) {
      return this.items.some(item => 
        item.product && item.product.id === record.product_id && 
        item.id === record.id,
      )
    },
    async onAddProduct(record) {
      try {
        const payload = {
          product_id: record.product_id,
          catalog_id: record.id,
        }
        await addProductToTag(this.channelId, this.activeTagKey, payload)
        this.$message.success(this.$t('product.productTags.manage.messages.add_success'))
        this.fetchData()
      } catch (error) {
        this.$message.error(this.$t('product.productTags.manage.messages.add_failed'))
      }
    },
    async onDeleteProduct(record) {
      try {
        await deleteProductFromTag(
          this.channelId, 
          this.activeTagKey, 
          record.product.id, 
          record.id,
        )
        this.$message.success(this.$t('product.productTags.manage.messages.remove_success'))
        this.fetchData()
      } catch (error) {
        this.$message.error(this.$t('product.productTags.manage.messages.remove_failed'))
      }
    },
    formatCurrency(value) {
      if (!value) return 'Rp 0'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.product-list {
  ::v-deep .ant-table-row {
    background-color: #fff !important;
  }
}
.object-fit-cover {
  object-fit: cover;
}
.pca-tab-product {
  ::v-deep {
    .ant-tabs-nav .ant-tabs-tab:hover,
    .ant-tabs-nav .ant-tabs-tab-active {
      color: #ff0a54;
    }

    .ant-tabs-ink-bar {
      background-color: #ff0a54;
    }
  }
}
</style>
